import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Employee } from 'data/users';
import { GET_EMPLOYEE, GET_ME } from 'services/api/employee';
import { GET_CSRF_TOKEN, LOGOUT } from 'services/api/auth';
import { setCsrfToken, setMicrosoftAccountId } from 'services/api/base';

interface AccessTokenContextProps {
  authUser: Employee | null;
  permissions: any[];
  isAdmin: boolean;
  isSessionLoading: boolean;
  setAuthUser: (user: Employee | null) => Employee | null;
  setPermissions: (permissions: any[]) => void;
  refreshUserData: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
  hasPermission: (permissionKey: string) => boolean;
  invalidateSession: () => void;
  logout: () => Promise<void>;
}

const AccessTokenContext = createContext<AccessTokenContextProps | undefined>(
  undefined
);

const getStoredJson = (key: string) => {
  const value = localStorage.getItem(key);
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

export const AccessTokenProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [authUser, setAuthenticatedUser] = useState<Employee | null>(
    getStoredJson('authUser')
  );
  const [permissionsData, setPermissionsData] = useState<any[]>([]);
  const [isAdmin, setIsAdmin] = useState<boolean>(
    localStorage.getItem('isAdmin') === 'true'
  );
  const [isSessionLoading, setIsSessionLoading] = useState<boolean>(true);

  const csrfTokenRef = useRef<string | null>(null);
  const csrfPromiseRef = useRef<Promise<string | null> | null>(null);
  const lastForegroundRefreshAtRef = useRef<number>(0);

  const isAuthRoute = useCallback(() => {
    const pathname = window.location.pathname;
    return pathname === '/' || pathname.startsWith('/auth/');
  }, []);

  const setPermissions = useCallback((permissions: any[]) => {
    setPermissionsData(permissions);
  }, []);

  const setAuthUser = useCallback((user: Employee | null): Employee | null => {
    setAuthenticatedUser(user);
    if (user) {
      localStorage.setItem('authUser', JSON.stringify(user));
      setIsAdmin(Boolean(user.isAdmin));
      localStorage.setItem('isAdmin', user.isAdmin ? 'true' : 'false');
    } else {
      localStorage.removeItem('authUser');
      localStorage.removeItem('isAdmin');
      setIsAdmin(false);
    }
    return user;
  }, []);

  const clearSessionState = useCallback(() => {
    setAuthUser(null);
    setPermissions([]);
  }, [setAuthUser, setPermissions]);

  const invalidateSession = useCallback(() => {
    // Local-only session invalidation (used on 401, etc). Do NOT call API logout here.
    clearSessionState();
    setCsrfToken(null);
    csrfTokenRef.current = null;
    setMicrosoftAccountId(null);
  }, [clearSessionState]);

  const hasPermission = useCallback(
    (permissionKey: string): boolean => {
      if (isAdmin) return true;
      return permissionsData.some(
        permission =>
          permission && permission.key === permissionKey && permission.isActive
      );
    },
    [isAdmin, permissionsData]
  );

  const ensureCsrfToken = useCallback(async (force = false) => {
    if (!force && csrfTokenRef.current) {
      return csrfTokenRef.current;
    }

    if (csrfPromiseRef.current) {
      return csrfPromiseRef.current;
    }

    csrfPromiseRef.current = (async () => {
      try {
        const response = await GET_CSRF_TOKEN();
        const payload = response?.data?.data || response?.data;
        const token = payload?.csrfToken || null;
        if (token) {
          csrfTokenRef.current = token;
          setCsrfToken(token);
        }
        return token;
      } catch (error) {
        console.error('Failed to obtain CSRF token', error);
        csrfTokenRef.current = null;
        setCsrfToken(null);
        return null;
      } finally {
        csrfPromiseRef.current = null;
      }
    })();

    return csrfPromiseRef.current;
  }, []);

  const refreshSession = useCallback(async (): Promise<boolean> => {
    setIsSessionLoading(true);
    try {
      const response = await GET_ME();
      const payload = response?.data;
      if (payload) {
        const homeAccountId = (payload as any)?.homeAccountId;
        if (typeof homeAccountId === 'string' && homeAccountId.trim()) {
          setMicrosoftAccountId(homeAccountId.trim());
        }

        setAuthUser(payload);
        const permissions =
          payload.roles
            ?.flatMap((role: any) => role.permissions || [])
            .filter(Boolean) || [];
        setPermissions(permissions);
        await ensureCsrfToken(true);
        return true;
      } else {
        clearSessionState();
        setCsrfToken(null);
        csrfTokenRef.current = null;
        return false;
      }
    } catch (error) {
      clearSessionState();
      setCsrfToken(null);
      csrfTokenRef.current = null;
      return false;
    } finally {
      setIsSessionLoading(false);
    }
  }, [clearSessionState, ensureCsrfToken, setAuthUser, setPermissions]);

  const refreshUserData = useCallback(async () => {
    if (!(authUser as any)?.id || authUser?._id) {
      return;
    }

    if (isAuthRoute()) {
      return;
    }

    try {
      const response = await GET_EMPLOYEE(
        (authUser as any)?.id || authUser?._id
      );
      const userData = response.data;
      const homeAccountId = (userData as any)?.homeAccountId;
      if (typeof homeAccountId === 'string' && homeAccountId.trim()) {
        setMicrosoftAccountId(homeAccountId.trim());
      }

      const permissions =
        userData.roles
          ?.flatMap((role: any) => role.permissions || [])
          .filter(Boolean) || [];
      setAuthUser(userData);
      setPermissions(permissions);
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  }, [
    (authUser as any)?.id || authUser?._id,
    isAuthRoute,
    setAuthUser,
    setPermissions
  ]);

  useEffect(() => {
    if (isAuthRoute()) {
      setIsSessionLoading(false);
      return;
    }
    refreshSession();
  }, [isAuthRoute, refreshSession]);

  useEffect(() => {
    if (!(authUser as any)?.id || authUser?._id) {
      return;
    }

    if (isAuthRoute()) {
      return;
    }

    refreshUserData();
    const refreshInterval = setInterval(refreshUserData, 5 * 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, [(authUser as any)?.id || authUser?._id, isAuthRoute, refreshUserData]);

  useEffect(() => {
    const handleRouteChange = () => {
      if (!(authUser as any)?.id || authUser?._id) return;
      if (!isAuthRoute()) {
        refreshUserData();
      }
    };

    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, [(authUser as any)?.id || authUser?._id, isAuthRoute, refreshUserData]);

  useEffect(() => {
    if (!(authUser as any)?.id || authUser?._id) return;
    if (isAuthRoute()) return;

    const handleForeground = () => {
      if (document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (now - lastForegroundRefreshAtRef.current < 30 * 1000) return;
      lastForegroundRefreshAtRef.current = now;
      refreshSession();
    };

    window.addEventListener('focus', handleForeground);
    document.addEventListener('visibilitychange', handleForeground);
    return () => {
      window.removeEventListener('focus', handleForeground);
      document.removeEventListener('visibilitychange', handleForeground);
    };
  }, [(authUser as any)?.id || authUser?._id, isAuthRoute, refreshSession]);

  const logout = useCallback(async () => {
    try {
      await LOGOUT();
    } catch (error) {
      console.error('Failed to log out:', error);
    } finally {
      clearSessionState();
      setCsrfToken(null);
      csrfTokenRef.current = null;
      localStorage.removeItem('microsoftAuthInProgress');
      localStorage.removeItem('microsoftAccessToken');
      localStorage.removeItem('microsoftRefreshToken');
      localStorage.removeItem('microsoftAccount');
      localStorage.removeItem('microsoftAccountId');
      localStorage.removeItem('microsoftTokenExpiry');
      setMicrosoftAccountId(null);
    }
  }, [clearSessionState]);

  const contextValue = useMemo(
    () => ({
      authUser,
      permissions: permissionsData,
      isAdmin,
      isSessionLoading,
      setAuthUser,
      setPermissions,
      refreshUserData,
      refreshSession,
      hasPermission,
      invalidateSession,
      logout
    }),
    [
      authUser,
      permissionsData,
      isAdmin,
      isSessionLoading,
      setAuthUser,
      setPermissions,
      refreshUserData,
      refreshSession,
      hasPermission,
      invalidateSession,
      logout
    ]
  );

  return (
    <AccessTokenContext.Provider value={contextValue}>
      {children}
    </AccessTokenContext.Provider>
  );
};

export const useAccessToken = (): AccessTokenContextProps => {
  const context = useContext(AccessTokenContext);
  if (!context) {
    throw new Error(
      'useAccessToken must be used within an AccessTokenProvider'
    );
  }
  return context;
};
